// Live-update helpers shared by WhatsAppChat.js (one thread) and
// WhatsAppOverview.js (the chat list).
//
// Every WhatsApp read and SSE event from the server carries a `seq` (the
// X-WhatsApp-Seq header / the event's `seq`, see server/whatsappLive.js): a
// number that goes up with every WhatsApp change. A response or event with
// seq N reflects every change up to N. Each row remembers the seq it was last
// set from (`rowSeq`), and a row is only replaced by something at least as
// new. So a slow full reload that left before an event can't undo what the
// event already showed, whatever order the answers arrive in.

// The seq a response was built at, or null from a server without it (then
// the merges below simply take what they're given, like before).
export const readSeq = (response) => {
  const value = Number(response?.headers?.get?.('X-WhatsApp-Seq'));
  return Number.isFinite(value) && value > 0 ? value : null;
};

const sameRow = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b);

// Merges rows from the server into `current`.
// - full: `incoming` is the complete list as of `seq`. Rows missing from it
//   are dropped unless they were set from something newer.
// - otherwise: `incoming` only has some rows; they're added or replaced.
// A row already set from a newer seq is kept as it is. Unchanged rows keep
// their old object, and if nothing changed at all, `current` itself is
// returned so React can skip the re-render.
// `rowSeq` (Map: key -> seq) is updated in place.
export const mergeRows = (current, incoming, { seq = null, full = false, rowSeq, keyOf = (row) => row.id, compare }) => {
  const currentByKey = new Map(current.map((row) => [keyOf(row), row]));
  const next = new Map();
  if (full) {
    if (seq === null) rowSeq.clear();
    for (const [key, row] of currentByKey) {
      if (seq !== null && (rowSeq.get(key) ?? -Infinity) > seq) next.set(key, row);
      else rowSeq.delete(key);
    }
  } else {
    for (const [key, row] of currentByKey) next.set(key, row);
  }
  for (const row of incoming) {
    const key = keyOf(row);
    const last = rowSeq.get(key);
    if (seq !== null && last !== undefined && last > seq) {
      // Newer than this answer: keep ours (already in `next` for a partial
      // merge; kept above for a full one).
      if (!next.has(key) && currentByKey.has(key)) next.set(key, currentByKey.get(key));
      continue;
    }
    const existing = currentByKey.get(key);
    next.set(key, existing && sameRow(existing, row) ? existing : row);
    if (seq !== null) rowSeq.set(key, seq);
  }
  const merged = [...next.values()];
  if (compare) merged.sort(compare);
  const unchanged = merged.length === current.length && merged.every((row, index) => row === current[index]);
  return unchanged ? current : merged;
};

// Applies tick changes from an SSE event: [{ messageId, status }] at `seq`.
// The server only sends a status when it moved forward (sent → delivered →
// read, or failed), so it's applied as given, unless the row was already set
// from something newer. Returns the new rows plus the ids this thread doesn't
// have yet (to fetch).
export const applyStatuses = (current, statuses, { seq = null, rowSeq }) => {
  const indexById = new Map(current.map((row, index) => [row.id, index]));
  const missing = [];
  let next = current;
  for (const { messageId, status } of statuses || []) {
    const index = indexById.get(messageId);
    if (index === undefined) {
      missing.push(messageId);
      continue;
    }
    const last = rowSeq.get(messageId);
    if (seq !== null && last !== undefined && last > seq) continue;
    if (next[index].status !== status) {
      if (next === current) next = current.slice();
      next[index] = { ...next[index], status };
    }
    if (seq !== null) rowSeq.set(messageId, seq);
  }
  return { rows: next, missing };
};

export const byMessageId = (a, b) => a.id - b.id;
// Newest thread first, like the server's ORDER BY m.created_at DESC.
export const byLatestMessage = (a, b) => String(b.last_message_at || '').localeCompare(String(a.last_message_at || ''));

// ---------------------------------------------------------------------------
// Outbox: replies the agent has sent that the server hasn't confirmed yet.
//
// Kept outside the React component, so a send keeps going (and a failure is
// still there to retry) if the agent switches to another chat or closes the
// call-history modal meanwhile. Sends to one customer go out one at a time,
// in the order they were typed. A failed send is never re-sent by itself;
// only the agent's Retry sends it again.
//
// Item: { localId, customerNumber, text, attachment, replyTo, sentBy,
//         createdAt, state: 'sending' | 'failed', error, serverId, uploaded }
// `serverId` is set as soon as the server's row id is known (from the SSE
// event, which can arrive before the HTTP response); the chat hides the
// "sending" bubble once that row is on screen.
const outbox = new Map(); // customerNumber -> items, oldest first
const outboxListeners = new Set();
const sendChains = new Map(); // customerNumber -> promise of the last send
let localCounter = 0;

const notify = (event) => {
  for (const listener of outboxListeners) listener(event);
};

export const subscribeOutbox = (listener) => {
  outboxListeners.add(listener);
  return () => outboxListeners.delete(listener);
};

export const getOutboxItems = (customerNumber) => outbox.get(customerNumber) || [];

const findItem = (localId) => {
  for (const items of outbox.values()) {
    const item = items.find((candidate) => candidate.localId === localId);
    if (item) return item;
  }
  return null;
};

const removeItem = (item) => {
  const items = (outbox.get(item.customerNumber) || []).filter((candidate) => candidate !== item);
  if (items.length) outbox.set(item.customerNumber, items);
  else outbox.delete(item.customerNumber);
  const preview = item.attachment?.previewUrl;
  if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
};

// Runs `send(item)` after the customer's earlier sends. `send` uploads any
// file and posts the message; it resolves to { message, seq } or throws.
const runSend = (item, send) => {
  const previous = sendChains.get(item.customerNumber) || Promise.resolve();
  const run = previous.then(async () => {
    if (!findItem(item.localId)) return; // discarded while waiting
    try {
      const { message, seq } = await send(item);
      removeItem(item);
      notify({ type: 'saved', customerNumber: item.customerNumber, item, message, seq });
    } catch (error) {
      if (item.serverId) {
        // The server already saved it (its event told us the row id); only
        // the reply got lost. Not a failure, and retrying would send twice.
        removeItem(item);
        notify({ type: 'saved', customerNumber: item.customerNumber, item, message: null, seq: null });
        return;
      }
      item.state = 'failed';
      item.error = error?.message || 'Send failed';
      notify({ type: 'failed', customerNumber: item.customerNumber, item });
    }
  });
  sendChains.set(item.customerNumber, run);
  run.finally(() => {
    if (sendChains.get(item.customerNumber) === run) sendChains.delete(item.customerNumber);
  });
  return run;
};

export const enqueueOutbound = ({ customerNumber, text, attachment = null, replyTo = null, sentBy = null, send }) => {
  localCounter += 1;
  const item = {
    localId: `local-${Date.now()}-${localCounter}`,
    customerNumber,
    text,
    attachment,
    replyTo,
    sentBy,
    createdAt: new Date().toISOString(),
    state: 'sending',
    error: '',
    serverId: null,
    uploaded: null,
  };
  outbox.set(customerNumber, [...getOutboxItems(customerNumber), item]);
  notify({ type: 'queued', customerNumber, item });
  runSend(item, send);
  return item;
};

export const retryOutbound = (localId, send) => {
  const item = findItem(localId);
  if (!item || item.state !== 'failed') return;
  item.state = 'sending';
  item.error = '';
  notify({ type: 'queued', customerNumber: item.customerNumber, item });
  runSend(item, send);
};

export const discardOutbound = (localId) => {
  const item = findItem(localId);
  if (!item || item.state !== 'failed') return;
  removeItem(item);
  notify({ type: 'discarded', customerNumber: item.customerNumber, item });
};

// The SSE event for a send names the row it became (clientMessageId).
export const resolveOutbound = (localId, serverId) => {
  const item = findItem(localId);
  if (!item || item.serverId) return;
  item.serverId = serverId;
  notify({ type: 'resolved', customerNumber: item.customerNumber, item });
};

// ---------------------------------------------------------------------------
// Opt-in browser timing logs: run localStorage.setItem('wa_timing', '1') in
// the console, then reload. Durations only.
const timingEnabled = () => {
  try {
    return window.localStorage.getItem('wa_timing') === '1';
  } catch {
    return false;
  }
};

// Logs how long after `startedAt` (performance.now()) the next frame was
// painted, i.e. when what was just put on screen became visible.
export const logTimingAtNextPaint = (label, startedAt) => {
  if (!timingEnabled() || typeof performance === 'undefined') return;
  requestAnimationFrame(() => {
    // eslint-disable-next-line no-console
    console.log(`[WA timing] ${label}: ${(performance.now() - startedAt).toFixed(1)} ms`);
  });
};
